import SearchspringListingBase from "@theme/searchspring-base";
import { fetchPriceData } from '@theme/theme-currency';
import { localizeUrl } from '@theme/localize-url';

/**
 * SsProductRecommendations
 * 
 * Custom element that fetches and renders Searchspring product recommendations.
 * Handles data context mapping for both Product Detail Pages (via dataset, global 
 * window state, or events) and Cart pages (fetching live cart IDs and tracking updates).
 */
export default class SsProductRecommendations extends SearchspringListingBase {
  #pageLoadId;
  #isCartPage;
  #handleCartUpdateBound;
  #isCartRefreshInFlight;
  #needsCartRefresh;

  constructor() {
    super();
    this.#pageLoadId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
    this.#isCartPage = false;
    this.#handleCartUpdateBound = this.#handleCartUpdate.bind(this);
    this.#isCartRefreshInFlight = false;
    this.#needsCartRefresh = false;
  }

  /**
   * Called when the element is inserted into the DOM.
   * Resolves component context attributes and handles race conditions for PDP context events.
   */
  async connectedCallback() {
    this.siteId = this.dataset.siteId?.trim();
    
    let rawTag = this.dataset.tags?.trim();
    this.tags = (rawTag && rawTag !== 'undefined') ? rawTag : 'you-may-also-like';
      
    this.limit = parseInt(this.dataset.limit || '4', 10);
    this.#isCartPage = this.#isCartTemplate();

    if (!this.siteId) {
      this.classList.add('hidden');
      return;
    }

    if (this.#isCartPage) {
      this.productIds = await this.#fetchCartProductIds();
      if (!this.productIds.length) {
        this.classList.add('hidden');
        return;
      }
    } else {
      const productId = this.dataset.productId?.trim();
      
      if (productId && productId !== 'undefined' && productId !== '') {
        this.productIds = [productId];
      } else if (window.landingPageProductId) {
        this.productIds = [String(window.landingPageProductId)];
      } else {
        this.productIds = [];
      }
    }

    this.cardTemplate = document.getElementById('ymal-product-card-template');
    this.placeholders = Array.from(this.querySelectorAll('.js-ss-placeholder'));

    if (!this.cardTemplate || !this.placeholders.length) return;

    if (this.#isCartPage) {
      document.addEventListener('cart:update', this.#handleCartUpdateBound);
    }

    if (this.productIds.length > 0) {
      await this.renderRecommendations();
    } else {
      window.addEventListener('landing-page:product-context', async (e) => {
        if (e.detail && e.detail.productId) {
          this.productIds = [String(e.detail.productId)];
          await this.renderRecommendations();
        }
      }, { once: true });
    }
  }

  /**
   * Called when the element is removed from the DOM.
   * Cleans up global document event listeners.
   */
  disconnectedCallback() {
    if (this.#isCartPage) {
      document.removeEventListener('cart:update', this.#handleCartUpdateBound);
    }
  }

  /**
   * Constructs URL parameters and fetches recommendation data from the Searchspring API.
   * @returns {Promise<Array>} Array of product result objects.
   */
  async apiRequest() {
    const products = this.productIds?.join(',') || '';
    
    if (!products && !this.tags) {
      return [];
    }

    const baseUrl = `https://${this.siteId}.a.searchspring.io`;
    const params = new URLSearchParams();
    
    params.set('tags', this.tags || '');
    if (products) {
      params.set('products', products);
    }
    
    params.set('limits', String(this.limit));
    params.set('userId', this.#getCookie('ssUserId'));
    params.set('sessionId', this.#getCookie('ssSessionId'));
    params.set('pageLoadId', this.#pageLoadId);

    const url = `${baseUrl}/boost/${this.siteId}/recommend?${params.toString()}`;

    try {
      const response = await fetch(url, { headers: { accept: 'application/json' } });
      if (!response.ok) throw new Error(`API Error: ${response.status}`);
      const json = await response.json();
      return json[0]?.results || [];
    } catch (error) {
      console.error('Searchspring API Request Failed:', error);
      return [];
    }
  }

  /**
   * Main rendering sequence orchestrator. 
   * Fetches API results, syncs live Shopify pricing via GraphQL, populates templates, and handles cleanup.
   */
  async renderRecommendations() {
    this.classList.add('is-updating');
    const results = await this.apiRequest();

    if (!results.length) {
      this.classList.add('hidden');
      this.classList.remove('is-updating');
      return;
    }

    this.classList.remove('hidden');

    const variantGids = results.map(res => {
      const initialVariant = res.variants?.data?.[0]?.mappings?.core || res.mappings.core;
      const vId = initialVariant.uid || initialVariant.variant_id;
      return `gid://shopify/ProductVariant/${vId}`;
    });

    let priceMap = new Map();
    try {
      const { nodes } = await fetchPriceData(variantGids, {
        GRAPHQL_URL: this.GRAPHQL_URL,
        STOREFRONT_TOKEN: this.STOREFRONT_TOKEN
      });
      nodes.forEach((node) => node?.id && priceMap.set(node.id, node));
    } catch (e) {
      console.warn("Live price fetch failed for recommendations", e);
    }

    results.forEach((product, index) => {
      const placeholder = this.placeholders[index];

      if (placeholder) {
        const vId = variantGids[index];
        const liveNode = priceMap.get(vId);

        placeholder.innerHTML = this.prepareTemplate(product, liveNode);
        placeholder.classList.remove('js-ss-placeholder');
        placeholder.classList.remove('hidden');

        const quickAdd = placeholder.querySelector('quick-add-component');
        if (quickAdd) {
          const variantCount = product.variants?.data?.length || 1;
          const variantClass = variantCount > 1 ? 'is-multiple-variants' : 'is-single-variant';
          quickAdd.classList.add(variantClass);
        }

        this.#bindSwatchEvents(placeholder);
      }
    });

    for (let i = results.length; i < this.placeholders.length; i++) {
      const placeholder = this.placeholders[i];
      if (placeholder) {
        placeholder.innerHTML = '';
        placeholder.classList.add('hidden');
      }
    }

    this.classList.remove('is-updating');

    if (typeof this.refreshYotpo === 'function') {
      this.refreshYotpo();
    }
  }

  /**
   * Attaches interaction listeners to variant swatches on rendered cards.
   * Handles image swapping, URL parameter updates, and triggers live price re-fetching.
   * @param {HTMLElement} card - The rendered product card DOM node.
   */
  #bindSwatchEvents(card) {
    const swatches = card.querySelectorAll('.js-swatch-item');

    swatches.forEach(swatch => {
      swatch.addEventListener('click', async (e) => {
        const swUrl = localizeUrl(swatch.dataset.variantUrl);
        const newImage = swatch.dataset.variantImage;
        let newHoverImage = swatch.dataset.variantHoverImage;
        const tempUrl = new URL(swUrl, window.location.origin);
        const newVariantId = tempUrl.searchParams.get('variant');

        swatches.forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');

        const mainImg = card.querySelector('.product-card-item__main-image');
        const hoverImg = card.querySelector('.product-card-item__hover-image');

        if (mainImg && newImage) {
          mainImg.src = newImage;
          mainImg.srcset = this.generateSrcset(newImage);
        }
        if (newHoverImage === 'null' || !newHoverImage) newHoverImage = newImage;
        if (hoverImg && newHoverImage) {
          hoverImg.src = newHoverImage;
          hoverImg.srcset = this.generateSrcset(newHoverImage);
        }

        const priceContainer = card.querySelector('.price-container');
        if (priceContainer && newVariantId) {
          card.classList.add('is-updating');
          try {
            const { nodes: liveData } = await fetchPriceData([`gid://shopify/ProductVariant/${newVariantId}`], {
              GRAPHQL_URL: this.GRAPHQL_URL,
              STOREFRONT_TOKEN: this.STOREFRONT_TOKEN
            });
            if (liveData && liveData[0]) {
              priceContainer.innerHTML = this.generateLivePriceMarkup(liveData[0]);
            }
          } catch (error) {
            console.error("Failed to update price:", error);
          } finally {
            card.classList.remove('is-updating');
          }
        }

        const formVariantInput = card.querySelector('input[name="id"]');
        if (formVariantInput) formVariantInput.value = newVariantId;

        const cardLinks = card.querySelectorAll('.js-ss-track');
        cardLinks.forEach(link => {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('variant', newVariantId);
          link.href = url.toString();
        });
      });
    });
  }

  /**
   * Replaces variables in the HTML template string with sanitized product data.
   * @param {Object} rawProduct - The raw product response object from Searchspring.
   * @param {Object} liveNode - Real-time price data object from Shopify GraphQL.
   * @returns {String} Compiled HTML markup string for insertion into the DOM.
   */
  prepareTemplate(rawProduct, liveNode) {
    const product = rawProduct.mappings.core;
    const initialVariant = rawProduct.variants?.data?.[0]?.mappings?.core || product;
    const productData = rawProduct.attributes;

    const activeImageUrl = initialVariant.imageUrl || product.imageUrl;
    const hoverImageUrl = rawProduct.imageHoverUrl || activeImageUrl;
    const productUrl = localizeUrl(initialVariant.url || product.url);

    return this.cardTemplate.innerHTML
      .replace(/\${productId}/g, product.uid || rawProduct.id)
      .replace(/\${variantId}/g, initialVariant.uid || initialVariant.variant_id)
      .replace(/\${productHandle}/g, productUrl.split('/').pop())
      .replace(/\${productUrl}/g, productUrl)
      .replace(/\${imageUrl}/g, activeImageUrl)
      .replace(/\${mainImageSrcset}/g, this.generateSrcset(activeImageUrl))
      .replace(/\${hoverImageUrl}/g, hoverImageUrl)
      .replace(/\${hoverImageSrcset}/g, this.generateSrcset(hoverImageUrl))
      .replace(/\${productTitle}/g, product.name)
      .replace(/\${productBadgeMarkup}/g, this.generateBadgeMarkup(productData))
      .replace(/\${swatchesMarkup}/g, this.generateSwatchesMarkup(productData))
      .replace(/\${ratingMarkup}/g, this.generateRatingMarkup(rawProduct))
      .replace(/\${priceMarkup}/g, this.generateLivePriceMarkup(liveNode));
  }

  #getCookie(name) {
    return document.cookie.split('; ').find(row => row.startsWith(`${name}=`))?.split('=')[1] || '';
  }

  #handleCartUpdate() {
    if (!this.#isCartPage) return;

    if (this.#isCartRefreshInFlight) {
      this.#needsCartRefresh = true;
      return;
    }

    void this.#refreshFromCart();
  }

  async #refreshFromCart() {
    this.#isCartRefreshInFlight = true;

    try {
      do {
        this.#needsCartRefresh = false;

        const latestProductIds = await this.#fetchCartProductIds();
        this.productIds = latestProductIds;

        if (!latestProductIds.length) {
          this.classList.add('hidden');
          continue;
        }

        await this.renderRecommendations();
      } while (this.#needsCartRefresh && this.isConnected);
    } finally {
      this.#isCartRefreshInFlight = false;
    }
  }

  #isCartTemplate() {
    const mainContent = document.querySelector('main[data-template]');
    const mainTemplate = mainContent instanceof HTMLElement ? mainContent.dataset.template?.toLowerCase() || '' : '';
    if (mainTemplate.includes('cart')) return true;

    if (document.body.classList.contains('template-cart') || document.body.classList.contains('template--cart')) {
      return true;
    }

    const currentPath = window.location.pathname.replace(/\/+$/, '');
    return currentPath === '/cart';
  }

  async #fetchCartProductIds() {
    try {
      const response = await fetch('/cart.js', { headers: { accept: 'application/json' } });
      if (!response.ok) throw new Error(`Cart API Error: ${response.status}`);

      const cart = await response.json();
      const items = Array.isArray(cart?.items) ? cart.items : [];

      return [...new Set(items.map(item => String(item.product_id || '').trim()).filter(Boolean))];
    } catch (error) {
      console.error('Searchspring Cart Fetch Error:', error);
      return [];
    }
  }
}

if (!customElements.get('ss-product-recommendations')) {
  customElements.define('ss-product-recommendations', SsProductRecommendations);
}